import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import './index.css';
import './portal-design.css';
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Timetable = lazy(() => import('./pages/Timetable'));
const Events = lazy(() => import('./pages/Events'));
const AcademicCalendar = lazy(() => import('./pages/AcademicCalendar'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Fees = lazy(() => import('./pages/Fees'));
const ExamReports = lazy(() => import('./pages/ExamReports'));
const FacultyChat = lazy(() => import('./pages/FacultyChat'));
const StudentPortalAssistant = lazy(() => import('./components/StudentPortalAssistant'));
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 minutes
        },
    },
});
// Protected Route component
function ProtectedRoute({ children }) {
    const { isAuthenticated, loading } = useAuth();
    if (loading) {
        return (<div className="sp-route-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>);
    }
    return isAuthenticated ? <>{children}</> : <Navigate to="/student-portal/login" replace/>;
}
function AppRoutes() {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    return (<Suspense fallback={<div className="sp-route-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"/>
        </div>}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="login" element={isAuthenticated ? <Navigate to="/student-portal/dashboard" replace/> : <Login />}/>
          <Route path="dashboard" element={<ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>}/>
          <Route path="profile" element={<ProtectedRoute>
                <Profile />
              </ProtectedRoute>}/>
          <Route path="change-password" element={<ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>}/>
          <Route path="timetable" element={<ProtectedRoute>
                <Timetable />
              </ProtectedRoute>}/>
          <Route path="events" element={<ProtectedRoute>
                <Events />
              </ProtectedRoute>}/>
          <Route path="calendar" element={<ProtectedRoute>
                <AcademicCalendar />
              </ProtectedRoute>}/>
          <Route path="attendance" element={<ProtectedRoute>
                <Attendance />
              </ProtectedRoute>}/>
          <Route path="fees" element={<ProtectedRoute>
                <Fees />
              </ProtectedRoute>}/>
          <Route path="exams" element={<ProtectedRoute>
                <ExamReports />
              </ProtectedRoute>}/>
          <Route path="attendance/history" element={<Navigate to="/student-portal/attendance" replace/>}/>
          <Route path="chat" element={<ProtectedRoute>
                <FacultyChat />
              </ProtectedRoute>}/>
          <Route path="chatbot" element={<Navigate to="/student-portal/dashboard" replace/>}/>
          <Route path="bu-assistant" element={<Navigate to="/student-portal/dashboard" replace/>}/>
          <Route index element={<Navigate to="/student-portal/dashboard" replace/>}/>
          <Route path="*" element={<Navigate to="/student-portal/dashboard" replace/>}/>
        </Routes>
      </AnimatePresence>
    </Suspense>);
}
function AppContent() {
    const { isAuthenticated } = useAuth();
    const { isDark } = useTheme();
    return (<>
      <Toaster position="top-right" theme={isDark ? 'dark' : 'light'} richColors closeButton toastOptions={{
            style: {
                background: isDark ? '#1f2937' : '#ffffff',
                border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
            },
        }}/>
      <AppRoutes />
      {isAuthenticated && (<Suspense fallback={null}>
          <StudentPortalAssistant />
        </Suspense>)}
    </>);
}
function StudentPortalRoutes() {
    return (<QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>);
}
export default StudentPortalRoutes;

